import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Tooltip as ReactTooltip } from "react-tooltip";

import { AppWrap, MotionWrap } from "../../wrapper";
import { urlFor, client } from "../../client";
import "./Skills.scss";

const Skills = () => {
  const [experiences, setExperiences] = useState([]);
  const [skills, setSkills] = useState([]);
  const [education, setEducation] = useState([
    {
      year: "2021 - 2024",
      works: [
        {
          name: "Software Engineering Technology - Artificial Intelligence",
          company: "Centennial College",
          desc: "Advanced Diploma",
        },
      ],
    },
  ]);

  useEffect(() => {
    const query = '*[_type == "experiences"]';
    const skillsQuery = '*[_type == "skills"]';

    client.fetch(query).then((data) => {
      setExperiences(data);
    });

    client.fetch(skillsQuery).then((data) => {
      setSkills(data);
    });
  }, []);

  // Animation variants for skills icons
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const skillItemVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8, 
      y: 50,
      rotate: -10
    },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      rotate: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.05,
      y: -5,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    },
    tap: {
      scale: 0.98,
      transition: {
        duration: 0.1
      }
    }
  };

  const iconVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.5,
      rotate: -180
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.3,
      rotate: 360,
      transition: {
        duration: 0.6,
        ease: "easeInOut"
      }
    }
  };

  const textVariants = {
    hidden: { 
      opacity: 0, 
      y: 20 
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        delay: 0.2
      }
    },
    hover: {
      y: -3,
      transition: {
        duration: 0.2
      }
    }
  };

  const experienceVariants = {
    hidden: { 
      opacity: 0, 
      x: -50 
    },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <>
      <motion.h2 
        className="head-text"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        Skills & <span>Experiences</span>
      </motion.h2>

      <div className="app__skills-container">
        <motion.div 
          className="app__skills-list"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {skills.map((skill, index) => (
            <motion.div
              className="app__skills-item app__flex"
              key={skill.name}
              variants={skillItemVariants}
              whileHover="hover"
              whileTap="tap"
              custom={index}
            >
              <div
                className="app__flex skill-icon-container"
                style={{ backgroundColor: skill.bgColor }}
              >
                <motion.img 
                  src={urlFor(skill.icon)} 
                  alt={skill.name}
                  className="skill-icon"
                  variants={iconVariants}
                  whileHover={{ 
                    scale: 1.3,
                    rotate: 360,
                    filter: "brightness(1.2) contrast(1.1)",
                    transition: { duration: 0.6, ease: "easeInOut" }
                  }}
                />
                <motion.div
                  className="skill-glow"
                  initial={{ opacity: 0, scale: 0 }}
                  whileHover={{ 
                    opacity: 1, 
                    scale: 1.5,
                    transition: { duration: 0.3 }
                  }}
                />
              </div>
              <motion.p 
                className="p-text skill-name"
                variants={textVariants}
                whileHover="hover"
              >
                {skill.name}
              </motion.p>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.div 
          className="app__skills-exp"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <motion.div 
            className="education_div"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <p>Experience</p>
          </motion.div>
          {experiences.map((experience, index) => (
            <motion.div 
              className="app__skills-exp-item" 
              key={experience.year}
              variants={experienceVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: index * 0.1 }}
            >
              <motion.div className="app__skills-exp-works">
                <motion.div 
                  className="app__skills-exp-year"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <p className="bold-text">{experience.year}</p>
                </motion.div>
                {experience.works.map((work, workIndex) => (
                  <React.Fragment key={work.name}>
                    <motion.div
                      className="app__skills-exp-work"
                      data-tip
                      data-for={work.name}
                      whileHover={{ 
                        x: 10,
                        transition: { duration: 0.2 }
                      }}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: (index * 0.1) + (workIndex * 0.1) }}
                    >
                      <h4 className="bold-text">{work.name}</h4>
                      <p className="p-text">{work.company}</p>
                    </motion.div>
                    <ReactTooltip
                      id={work.name}
                      effect="solid"
                      arrowColor="#fff"
                      className="skills-tooltip"
                    >
                      {work.desc}
                    </ReactTooltip>
                  </React.Fragment>
                ))}
              </motion.div>
            </motion.div>
          ))}
          
          <motion.hr 
            className="HR"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          />
          
          <motion.div 
            className="education_div"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            <p>Education</p>
          </motion.div>
          {education.map((experience, index) => (
            <motion.div 
              className="app__skills-exp-item" 
              key={experience.year}
              variants={experienceVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.8 + (index * 0.1) }}
            >
              <motion.div className="app_skills-exp-works2">
                <motion.div 
                  className="app__skills-exp-year"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <p className="bold-text">{experience.year}</p>
                </motion.div>
                {experience.works.map((work, workIndex) => (
                  <React.Fragment key={work.name}>
                    <motion.div
                      className="app__skills-exp-work"
                      data-tip
                      data-for={work.name}
                      whileHover={{ 
                        x: 10,
                        transition: { duration: 0.2 }
                      }}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.9 + (index * 0.1) + (workIndex * 0.1) }}
                    >
                      <h4 className="bold-text">{work.name}</h4>
                      <p className="p-text">{work.company}</p>
                    </motion.div>
                    <ReactTooltip
                      id={work.name}
                      effect="solid"
                      arrowColor="#fff"
                      className="skills-tooltip"
                    >
                      {work.desc}
                    </ReactTooltip>
                  </React.Fragment>
                ))}
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </>
  );
};

export default AppWrap(
  MotionWrap(Skills, "app__skills"),
  "skills",
  "app__whitebg"
);
